import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowUpRight, FileText, ExternalLink, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";

const LAWS = [
  {
    n: "00",
    short: "Novo · 2026.",
    title: "Izmjene i dopuna Zakona o postupanju s nezakonito izgrađenim zgradama",
    ref: "NN 48/2026 · na snazi od 16.05.2026.",
    highlight: true,
    desc:
      "Najvažnije izmjene zakona o legalizaciji iz 2026. godine — Sabor izglasao 30.04.2026., na snazi od 16.05.2026. (NN 48/2026). Ključne novosti: ukidanje prekluzivnog roka za podnošenje zahtjeva, ograničenje legalizacije isključivo na objekte vidljive na DOF5/2011, potpuna digitalizacija postupka kroz ISPU i eDozvola te povećanje naknade za bespravnu gradnju.",
    bullets: [
      "Ukinut prekluzivni rok — zahtjev se može podnijeti u bilo kojem trenutku",
      "Legalizacija samo za objekte izgrađene do 21.06.2011. (vidljivi na DOF5/2011)",
      "Isključene zone: prometni i infrastrukturni koridori, zaštićena priroda, UNESCO, kulturna dobra, pomorsko dobro, parkovi prirode, državno, šumsko i vodno zemljište",
      "Postupak u potpunosti digitaliziran — ISPU i eDozvola",
      "Obavezna potvrda DGU o vidljivosti na DOF5/2011 (GML/SML format)",
      "Osnovica naknade za 2026. povećana na 170 € (+6,25 %)",
    ],
  },
  {
    n: "01",
    short: "ZPNIZ",
    title: "Zakon o postupanju s nezakonito izgrađenim zgradama",
    ref: "NN 86/12, 143/13, 65/17, 14/19, 48/26",
    desc:
      "Temeljni propis kojim se uređuju uvjeti, postupak i pravne posljedice ozakonjenja nezakonito izgrađenih zgrada u Republici Hrvatskoj. Posljednja izmjena: NN 48/2026.",
  },
  {
    n: "02",
    short: "Pravilnik",
    title: "Pravilnik o jednostavnim i drugim građevinama",
    ref: "NN 112/17, 34/18, 36/19, 98/19, 31/20, 74/22",
    desc:
      "Propisuje koje se građevine i radovi mogu graditi bez građevinske dozvole odnosno glavnog projekta — bitno za ocjenu obuhvata legalizacije.",
  },
  {
    n: "03",
    short: "ZoG",
    title: "Zakon o gradnji",
    ref: "NN 153/13, 20/17, 39/19, 125/19",
    desc:
      "Uređuje projektiranje, gradnju, uporabu i održavanje građevina, kao i provedbu upravnih i drugih postupaka u vezi s gradnjom.",
  },
  {
    n: "04",
    short: "ZoPU",
    title: "Zakon o prostornom uređenju",
    ref: "NN 153/13, 65/17, 114/18, 39/19, 98/19",
    desc:
      "Definira ciljeve, načela i sustav prostornog uređenja te dokumente prostornog uređenja relevantne za ocjenu mogućnosti legalizacije.",
  },
  {
    n: "05",
    short: "DG/KOM",
    title: "Zakon o državnoj izmjeri i katastru nekretnina",
    ref: "NN 112/18, 39/22",
    desc:
      "Regulira katastar nekretnina, geodetske elaborate i upis objekata u katastarski operat nakon ishođenja rješenja o izvedenom stanju.",
  },
  {
    n: "06",
    short: "ZK",
    title: "Zakon o zemljišnim knjigama",
    ref: "NN 63/19, 128/22",
    desc:
      "Uređuje ustroj, vođenje i upis u zemljišne knjige — završni korak nakon legalizacije za upis stvarnih prava.",
  },
];

const CATEGORIES = [
  { label: "Zahtjevna zgrada", area: "preko 400 m² GBP ili sa više od 3 nadzemne etaže" },
  { label: "Manje zahtjevna zgrada", area: "do 400 m² GBP / do 600 m² GBP poljoprivredna" },
  { label: "Jednostavna zgrada", area: "do 100 m² GBP, do 2 nadzemne etaže" },
  { label: "Pomoćna zgrada", area: "do 50 m² GBP, jedna etaža, uz postojeću zgradu" },
];

export default function PropisiPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Koji zakon uređuje legalizaciju nezakonito izgrađenih zgrada?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Temeljni propis je Zakon o postupanju s nezakonito izgrađenim zgradama (NN 86/12, 143/13, 65/17, 14/19, 48/26).",
        },
      },
      {
        "@type": "Question",
        name: "Je li ukinut rok za podnošenje zahtjeva?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Prema izmjenama iz 2026. godine, prekluzivni rok je ukinut, uz ostale zakonske uvjete postupka.",
        },
      },
    ],
  };

  return (
    <div data-testid="propisi-page">
      <Seo
        title="Zakonski propisi legalizacije"
        description="Pregled važećih zakonskih propisa za legalizaciju nezakonito izgrađenih objekata u Republici Hrvatskoj, uključujući izmjene iz 2026. godine."
        path="/zakonski-propisi"
        jsonLd={jsonLd}
      />
      {/* HERO */}
      <section className="border-b border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="overline text-ink-soft flex items-center gap-3">
                <span className="w-8 h-px bg-ink-soft/40" /> Zakonski okvir
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold text-ink mt-6">
                Zakonski<br />propisi i<br />
                <span className="text-ink/40">primjenjivi okvir.</span>
              </h1>
            </div>
            <div className="md:col-span-4 text-ink-soft leading-relaxed">
              Pregled važećih propisa Republike Hrvatske koji uređuju legalizaciju
              nezakonito izgrađenih zgrada. Sadržaj služi isključivo u informativne
              svrhe.
            </div>
          </div>
        </div>

        {/* 2026 update banner */}
        <div className="border-t border-hair bg-[#FFF5F4]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
            <div className="flex items-start gap-4">
              <span className="bg-[#D92D20] text-white font-brand uppercase text-[11px] tracking-[0.22em] px-3 py-1.5">
                Novo · 2026.
              </span>
              <p className="text-ink text-sm sm:text-[15px] leading-relaxed">
                <span className="font-semibold">Izmjene Zakona o legalizaciji (NN 48/2026)</span> — Sabor izglasao 30.04.2026., na snazi od 16.05.2026.
                Ukinut prekluzivni rok, legalizacija samo za objekte vidljive na DOF5/2011, postupak digitaliziran kroz ISPU/eDozvola.
              </p>
            </div>
            <a
              href="#law-00"
              onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="law-trigger-00"]')?.click(); document.querySelector('[data-testid="law-trigger-00"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#D92D20] border-b border-[#D92D20]/40 hover:border-[#D92D20] whitespace-nowrap"
            >
              Pročitaj sve izmjene
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24">
        <div className="overline text-ink-soft">Kategorije zgrada</div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold text-ink mt-3 leading-tight">
          Razvrstavanje po veličini i složenosti.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-ink/10 mt-10 border border-ink/10">
          {CATEGORIES.map((c, i) => (
            <div key={c.label} className="bg-white p-6">
              <div className="font-display text-4xl text-ink/15 font-bold">0{i + 1}</div>
              <div className="font-display font-semibold text-ink mt-3">{c.label}</div>
              <div className="text-ink-soft text-sm mt-1.5 leading-relaxed">{c.area}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LAWS ACCORDION */}
      <section className="border-t border-hair surface">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <div className="overline text-ink-soft">Propisi</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
                Važeći zakoni i pravilnici.
              </h2>
            </div>
            <div className="md:col-span-5 text-ink-soft leading-relaxed">
              Klikom na svaki zakon otvarate kratki opis i službene oznake objave u
              Narodnim novinama.
            </div>
          </div>

          <div className="mt-12 bg-white border border-hair">
            <Accordion type="single" collapsible className="w-full" data-testid="laws-accordion">
              {LAWS.map((l) => (
                <AccordionItem
                  key={l.n}
                  value={l.n}
                  className={`border-b border-hair last:border-b-0 ${l.highlight ? "bg-[#FFF5F4]" : ""}`}
                >
                  <AccordionTrigger
                    className="px-6 py-6 hover:no-underline data-[state=open]:bg-secondary/40"
                    data-testid={`law-trigger-${l.n}`}
                  >
                    <div className="flex items-start gap-6 text-left w-full">
                      <span className={`font-display text-2xl font-bold leading-none w-10 shrink-0 ${l.highlight ? "text-[#D92D20]" : "text-ink/25"}`}>
                        {l.highlight ? <Sparkles className="w-6 h-6" strokeWidth={1.5} /> : l.n}
                      </span>
                      <div className="flex-1">
                        <div className={`overline ${l.highlight ? "text-[#D92D20]" : "text-ink-soft"}`}>{l.short}</div>
                        <div className="font-display text-lg sm:text-xl font-semibold text-ink mt-1.5 tracking-tight">
                          {l.title}
                        </div>
                      </div>
                      <span className="hidden md:inline text-xs text-ink-soft shrink-0 mt-2">
                        {l.ref}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <div className="md:pl-16 max-w-3xl">
                      <p className="text-ink leading-relaxed">{l.desc}</p>
                      {l.bullets && (
                        <ul className="mt-4 space-y-2">
                          {l.bullets.map((b, i) => (
                            <li key={i} className="flex gap-3 text-ink text-[15px] leading-relaxed">
                              <span className="text-[#D92D20] font-bold mt-0.5">◆</span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="md:hidden text-xs text-ink-soft mt-3">{l.ref}</div>
                      <a
                        href={l.highlight ? "https://narodne-novine.nn.hr/eli/sluzbeni/2026/48/583" : "https://narodne-novine.nn.hr"}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 mt-5 text-sm text-ink font-medium border-b border-ink/40 pb-0.5 hover:border-ink"
                      >
                        Narodne novine <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.75} />
                      </a>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="mt-10 border border-ink/15 bg-white p-6 flex items-start gap-4">
            <FileText className="w-5 h-5 text-ink mt-0.5 shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-ink-soft leading-relaxed">
              <span className="text-ink font-medium">Napomena:</span> Sadržaj ove stranice
              je informativan i ne predstavlja pravni savjet. Za konkretnu situaciju
              kontaktirajte naš tim ili odvjetnika.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold text-ink leading-tight">
              Niste sigurni primjenjuje li se legalizacija na vaš slučaj?
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/prijava"
              data-testid="propisi-cta"
              className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-[#112240] transition-colors"
            >
              Provjerite slučaj
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
